import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * `value`, but only once it has held still for `delayMs` — plus a `flush` that
 * adopts the latest value right away.
 *
 * The export preview keys its render query on this (issue #11): a burst of
 * setting changes costs one server render, not one per click, and `flush`
 * lets the export button ask for the current settings' document without
 * waiting out the pause.
 *
 * Change detection is by identity, so callers pass a memoized value.
 */
export function useDebouncedValue<T>(value: T, delayMs: number): [T, () => void] {
  const [debounced, setDebounced] = useState(value);
  const latestRef = useRef(value);
  latestRef.current = value;

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);

  // A flush leaves the timer running; when it fires it sets the value it
  // already holds, which React bails out of.
  const flush = useCallback(() => setDebounced(latestRef.current), []);

  return [debounced, flush];
}
